import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { dedupeRequest, getCacheKey } from '../utils/queryDeduplication';
import { calculateLevel } from '../utils/xpLevelUtils';
import { getUserFacingErrorMessage } from '../utils/sanitizeErrorForLog';
import { Icon } from './Icon';
import JellyfishLoader from './JellyfishLoader';
import './ChecklistLogger.css';

// Level-up success popup (same style as other success popups)
const LevelUpSuccessPopup = ({ show, level, onComplete }) => {
  useEffect(() => {
    if (!show || !level) return;
    const timer = setTimeout(() => {
      onComplete?.();
    }, 3000);
    return () => clearTimeout(timer);
  }, [show, level, onComplete]);

  if (!show || !level) return null;

  return createPortal(
    <div className="level-up-success-popup">
      <Icon name="Check" className="icon--sm" />
      <span>Level up! You reached level {level}</span>
    </div>,
    document.body
  );
};

// Floating XP Animation component
const FloatingXPAnimation = ({ show, xpAmount, onComplete }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (show) {
      setVisible(true);

      // Animation sequence - match the biohack added popup timing
      const timer = setTimeout(() => {
        setVisible(false);
        setTimeout(onComplete, 200); // Allow fade out time
      }, 2000); // 2 seconds like the biohack added popup

      return () => clearTimeout(timer);
    }
  }, [show, onComplete]);

  if (!show) return null;

  return createPortal(
    <div className={`floating-xp ${visible ? 'visible' : ''}`}>
      <span className="floating-xp-icon"></span>
      <span>+{xpAmount} XP</span>
    </div>,
    document.body
  );
};

const formatTimeDisplay = (value) => {
  if (!value) return '';
  const parts = value.split(':');
  if (parts.length < 2) return value;
  const hour = parseInt(parts[0], 10);
  const minute = parts[1];
  if (Number.isNaN(hour)) return value;
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const normalizedHour = ((hour + 11) % 12) + 1;
  return `${normalizedHour}:${minute} ${suffix}`;
};

const buildScheduleEntries = (rows = [], protocolsList = [], biohackList = []) => {
  const protocolMap = new Map();
  const hackMap = new Map();
  const protocolHasSchedule = new Map();
  const scheduledEntries = [];
  const unscheduledEntries = [];

  (protocolsList || []).forEach((protocol) => {
    if (!protocol?.id) return;
    const key = String(protocol.id);
    protocolMap.set(key, protocol);
    (protocol.hacks || []).forEach((hack) => {
      if (hack?.id) {
        hackMap.set(`${key}:${String(hack.id)}`, hack);
      }
    });
  });

  (biohackList || []).forEach((hack) => {
    if (hack?.id) {
      hackMap.set(`global:${String(hack.id)}`, hack);
    }
  });

  // Build scheduled entries from rows
  // Only include schedules for protocols that are in the protocolsList (i.e., active protocols)
  rows
    .filter((row) => row.protocol_id && row.time)
    .forEach((row) => {
      const protocolKey = String(row.protocol_id);
      const protocol = protocolMap.get(protocolKey);
      // Only add schedule entries for active protocols (protocols that exist in protocolsList)
      if (!protocol) {
        return; // Skip schedules for inactive protocols
      }
      protocolHasSchedule.set(protocolKey, true);
      const protocolName = protocol.name || 'Protocol';
      let hackName = null;
      if (row.hack_id) {
        const hackKey = `${protocolKey}:${String(row.hack_id)}`;
        const hack =
          hackMap.get(hackKey) || hackMap.get(`global:${String(row.hack_id)}`);
        hackName = hack?.name || null;
      }
      scheduledEntries.push({
        id: row.id || `${protocolKey}-${row.hack_id || 'protocol'}-${row.time}`,
        protocolId: protocolKey,
        hackId: row.hack_id ? String(row.hack_id) : null,
        time: row.time,
        protocolName,
        hackName,
        isDefault: false
      });
    });

  // Build unscheduled entries for protocols without schedules
  (protocolsList || []).forEach((protocol) => {
    if (!protocol?.id) return;
    const key = String(protocol.id);
    if (protocolHasSchedule.get(key)) return; // Skip protocols that have schedules
    
    // Include all hacks from unscheduled protocols
    if (protocol.hacks && protocol.hacks.length > 0) {
      protocol.hacks.forEach((hack) => {
        if (hack?.id) {
          unscheduledEntries.push({
            id: `unscheduled-${key}-${String(hack.id)}`,
            protocolId: key,
            hackId: String(hack.id),
            time: null, // No time for unscheduled items
            protocolName: protocol.name || 'Protocol',
            hackName: hack.name || null,
            isDefault: false,
            isUnscheduled: true
          });
        }
      });
    }
  });

  // Sort scheduled entries
  const sortedScheduled = scheduledEntries.sort((a, b) => {
    if (!a.hackId && b.hackId) return -1;
    if (a.hackId && !b.hackId) return 1;
    return a.time.localeCompare(b.time);
  });

  // Sort unscheduled entries by protocol name, then hack name
  const sortedUnscheduled = unscheduledEntries.sort((a, b) => {
    if (a.protocolName !== b.protocolName) {
      return a.protocolName.localeCompare(b.protocolName);
    }
    if (a.hackName && b.hackName) {
      return a.hackName.localeCompare(b.hackName);
    }
    return 0;
  });

  return {
    scheduled: sortedScheduled,
    unscheduled: sortedUnscheduled
  };
};

const ChecklistLogger = ({ open, onClose, onLogChange, isPageMode = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [protocols, setProtocols] = useState([]);
  const [biohacks, setBiohacks] = useState([]);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);
  const [logged, setLogged] = useState({}); // { [protocolId]: Set(hackId) }
  // Removed completedOpen state - completed items are now always visible
  const [actionLoading, setActionLoading] = useState({}); // { [protocolId_hackId]: bool }
  const [floatingXP, setFloatingXP] = useState({ show: false, amount: 0 }); // Floating XP animation
  const [showLevelUpPopup, setShowLevelUpPopup] = useState(false);
  const [levelUpLevel, setLevelUpLevel] = useState(null);
  const [scheduleEntries, setScheduleEntries] = useState({ scheduled: [], unscheduled: [] });
  const [scheduleWarning, setScheduleWarning] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [disclaimerText, setDisclaimerText] = useState('');
  const [disclaimerVersion, setDisclaimerVersion] = useState('');
  const [acceptResponsibility, setAcceptResponsibility] = useState(false);
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);
  const [pendingLogEntry, setPendingLogEntry] = useState(null);

  // Click outside to close functionality (disabled in page mode)
  useEffect(() => {
    // Don't add click-outside listener when in page mode
    if (isPageMode) return;
    
    const handleClickOutside = (event) => {
      // Don't close if disclaimer modal is open
      if (showDisclaimerModal) {
        return;
      }
      // Don't close if clicking on the disclaimer modal
      if (event.target.closest('.modal-overlay') || event.target.closest('.modal-content')) {
        return;
      }
      // Only close if clicking outside the entire checklist logger overlay
      if (open && !event.target.closest('.checklist-logger-overlay')) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onClose, isPageMode, showDisclaimerModal]);

  useEffect(() => {
    const handleScheduleUpdated = () => {
      setReloadKey((prev) => prev + 1);
    };

    window.addEventListener('logging-schedule-updated', handleScheduleUpdated);
    return () => {
      window.removeEventListener('logging-schedule-updated', handleScheduleUpdated);
    };
  }, []);

  // Helper: get today's date in YYYY-MM-DD
  const getToday = () => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  };

  // Helper: check if user levelled up after XP gain and show success popup
  const checkAndShowLevelUp = async (profile, xpGained) => {
    if (!profile || xpGained <= 0) return;
    const xpAfter = profile.xp ?? 0;
    const xpBefore = Math.max(0, xpAfter - xpGained);
    try {
      const oldLevel = await calculateLevel(xpBefore);
      const newLevel = profile.user_level ?? (await calculateLevel(xpAfter));
      if (newLevel > oldLevel) {
        setLevelUpLevel(newLevel);
        setShowLevelUpPopup(true);
      }
    } catch (e) {
      console.warn('[ChecklistLogger] Level-up check failed:', e);
    }
  };

  // Fetch protocols, hacks, and today's logs
  useEffect(() => {
    if (!open) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      setScheduleWarning(null);
      try {
        // Use getSession() to avoid GET /auth/v1/user (we only need user id)
        const { data: { session } } = await supabase.auth.getSession();
        const user = session?.user;
        setUserId(user ? user.id : null);
        if (!user) {
          setError('User not signed in');
          setScheduleEntries([]);
          setLoading(false);
          return;
        }
        
        // Fetch biohacks with deduplication
        const biohacksCacheKey = getCacheKey('fetchBiohacks', 'checklistLogger');
        const { data: biohacksData } = await dedupeRequest(biohacksCacheKey, async () => {
          return await supabase
            .from('biohacks')
            .select('id, name, image_url');
        });
        setBiohacks(biohacksData || []);
        
        // Fetch user profile with deduplication
        const profileCacheKey = getCacheKey('fetchUserProfile', user.id);
        const { data: profileData, error: profileError } = await dedupeRequest(profileCacheKey, async () => {
          return await supabase
            .from('user_profiles')
            .select('profile_picture_url, username')
            .eq('user_id', user.id)
            .maybeSingle();
        }, { caller: 'ChecklistLogger' });
        
        if (profileError) {
          console.error('[ChecklistLogger] Error fetching user profile:', profileError);
        }
        
        // Fetch user's active protocols using protocols_status with deduplication
        const protocolsCacheKey = getCacheKey('fetchProtocolsStatus', user.id);
        const { data: protocolStatusData } = await dedupeRequest(protocolsCacheKey, async () => {
          return await supabase
            .from('protocols_status')
            .select(`
              *,
              protocols:protocol_id (
                *,
                protocol_hacks ( hack_id, position )
              )
            `)
            .eq('user_id', user.id)
            .eq('is_active', true);
        });
        
        const protocolsWithHacks = (protocolStatusData || []).map(status => {
          const protocol = status.protocols;
          if (!protocol) return null;
          
          const hacks = (protocol.protocol_hacks || [])
            .sort((a, b) => a.position - b.position)
            .map(h => biohacksData.find(b => b.id === h.hack_id))
            .filter(Boolean);
          return { ...protocol, hacks };
        }).filter(Boolean);
        setProtocols(protocolsWithHacks);
        // Fetch saved logging schedule with deduplication
        try {
          const scheduleCacheKey = getCacheKey('fetchLoggingSchedules', user.id);
          const { data: scheduleData, error: scheduleError } = await dedupeRequest(scheduleCacheKey, async () => {
            return await supabase
              .from('logging_schedules')
              .select('id, protocol_id, hack_id, time')
              .eq('user_id', user.id)
              .eq('is_active', true);
          });

          if (scheduleError) {
            if (scheduleError.message && scheduleError.message.includes('logging_schedules')) {
              setScheduleWarning('Logging schedule is not configured yet.');
            } else {
              console.error('ChecklistLogger: failed to fetch logging schedule', scheduleError);
            }
            setScheduleEntries(buildScheduleEntries([], protocolsWithHacks, biohacksData || []));
          } else {
            setScheduleEntries(buildScheduleEntries(scheduleData || [], protocolsWithHacks, biohacksData || []));
          }
        } catch (scheduleCatchErr) {
          console.error('ChecklistLogger: unexpected error loading logging schedule', scheduleCatchErr);
          setScheduleEntries(buildScheduleEntries([], protocolsWithHacks, biohacksData || []));
        }
        // Fetch today's logs with deduplication
        const today = getToday();
        const logsCacheKey = getCacheKey('fetchLoggingHackEntries', [user.id, today]);
        const { data: logsToday } = await dedupeRequest(logsCacheKey, async () => {
          return await supabase
            .from('logging_hack_entries')
            .select('protocol_id, hack_id')
            .eq('user_id', user.id)
            .eq('date', today);
        });
        // Build logged state
        const loggedMap = {};
        protocolsWithHacks.forEach(p => {
          loggedMap[p.id] = new Set();
        });
        (logsToday || []).forEach(entry => {
          if (entry.protocol_id && entry.hack_id && loggedMap[entry.protocol_id]) {
            loggedMap[entry.protocol_id].add(entry.hack_id);
          }
        });
        setLogged(loggedMap);
      } catch (err) {
        setError('Failed to load protocols.');
        setScheduleEntries({ scheduled: [], unscheduled: [] });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [open, reloadKey]);

  const protocolsById = useMemo(() => {
    const map = new Map();
    protocols.forEach((protocol) => {
      if (!protocol?.id) return;
      map.set(String(protocol.id), protocol);
    });
    return map;
  }, [protocols]);

  const isEntryCompleted = (entry) => {
    const protocol = protocolsById.get(entry.protocolId);
    if (!protocol) return false;
    const loggedSet = logged[protocol.id] || new Set();
    if (entry.hackId) {
      return loggedSet.has(entry.hackId);
    }
    const hackIds = (protocol.hacks || []).map((hack) => hack?.id).filter(Boolean);
    if (hackIds.length === 0) return false;
    return hackIds.every((id) => loggedSet.has(id));
  };

  const isEntryLoading = (entry) => {
    if (entry.hackId) {
      return !!actionLoading[`${entry.protocolId}_${entry.hackId}`];
    }
    const protocol = protocolsById.get(entry.protocolId);
    if (!protocol) return false;
    return (protocol.hacks || []).some((hack) => actionLoading[`${protocol.id}_${hack.id}`]);
  };

  // Check if user has accepted the checklist log disclaimer
  const checkDisclaimerAcceptance = async (userId) => {
    if (!userId) {
      return false;
    }
    try {
      const { data, error } = await supabase
        .from('disclaimers_log')
        .select('id, accepted_at, do_not_show_again')
        .eq('user_id', userId)
        .eq('disclaimer_type', 'checklist_log_schedule')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        return false;
      }

      if (!data) {
        return false;
      }
      
      if (data.do_not_show_again === true) {
        return true;
      }
      
      return false;
    } catch (error) {
      return false;
    }
  };

  // Fetch disclaimer text from disclaimers table
  const fetchDisclaimerText = async () => {
    try {
      const { data, error } = await supabase
        .from('disclaimers')
        .select('text, version')
        .eq('type', 'checklist_log_schedule')
        .eq('latest', true)
        .maybeSingle();
      
      if (error) {
        return null;
      }

      if (!data) {
        return null;
      }

      return data;
    } catch (error) {
      return null;
    }
  };

  // Store disclaimer acceptance
  const storeDisclaimerAcceptance = async (userId, version, doNotShow) => {
    if (!userId) return false;
    try {
      const { error } = await supabase
        .from('disclaimers_log')
        .insert({
          user_id: userId,
          disclaimer_type: 'checklist_log_schedule',
          disclaimer_version: version,
          accepted_at: new Date().toISOString(),
          do_not_show_again: doNotShow
        });

      if (error) {
        console.error('Error storing disclaimer acceptance:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error storing disclaimer acceptance:', error);
      return false;
    }
  };

  // Internal function to perform logging (after disclaimer check)
  const performLogInternal = async (entry) => {
    const protocol = protocolsById.get(entry.protocolId);
    if (!protocol) return;
    if (isEntryLoading(entry)) return;
    const completed = isEntryCompleted(entry);
    if (entry.hackId) {
      await handleHackCheck(protocol.id, entry.hackId, !completed);
    } else {
      const hackIds = (protocol.hacks || []).map((hack) => hack?.id).filter(Boolean);
      if (hackIds.length === 0) return;
      await handleProtocolCheck(protocol.id, hackIds, !completed);
    }
  };

  const handlePlanLog = async (entry) => {
    if (!userId) {
      alert('You must be logged in to log entries.');
      return;
    }

    // Check if user has accepted the disclaimer
    const hasAcceptedDisclaimer = await checkDisclaimerAcceptance(userId);
    
    if (!hasAcceptedDisclaimer) {
      // Fetch disclaimer text and show modal
      const disclaimer = await fetchDisclaimerText();
      if (disclaimer && disclaimer.text) {
        setDisclaimerText(disclaimer.text);
        setDisclaimerVersion(disclaimer.version || 'v1.0');
        setPendingLogEntry(entry);
        setShowDisclaimerModal(true);
        setAcceptResponsibility(false);
        setDoNotShowAgain(false);
        return;
      }
    }

    // User has accepted disclaimer, proceed with logging
    await performLogInternal(entry);
  };

  // Handle disclaimer acceptance
  const handleDisclaimerAccept = async () => {
    if (!acceptResponsibility) {
      return;
    }

    try {
      if (!userId) {
        setShowDisclaimerModal(false);
        return;
      }

      const acceptanceStored = await storeDisclaimerAcceptance(userId, disclaimerVersion, doNotShowAgain);
      
      if (acceptanceStored) {
        setShowDisclaimerModal(false);
        setAcceptResponsibility(false);
        setDoNotShowAgain(false);
        
        // Proceed with logging if there's a pending entry
        if (pendingLogEntry) {
          await performLogInternal(pendingLogEntry);
          setPendingLogEntry(null);
        }
      } else {
        alert('Error storing disclaimer acceptance. Please try again.');
      }
    } catch (error) {
      console.error('Error handling disclaimer acceptance:', error);
      alert('Error storing disclaimer acceptance. Please try again.');
    }
  };

  const handleDisclaimerCancel = () => {
    setShowDisclaimerModal(false);
    setAcceptResponsibility(false);
    setDoNotShowAgain(false);
    setPendingLogEntry(null);
  };

  // Handle check/uncheck for a hack (persistent)
  const handleHackCheck = async (protocolId, hackId, checked) => {
    setActionLoading(prev => ({ ...prev, [`${protocolId}_${hackId}`]: true }));
    try {
      if (checked) {
        // Insert log
        const { error: insertError } = await supabase.from('logging_hack_entries').insert({
          user_id: userId,
          protocol_id: protocolId,
          hack_id: hackId,
          date: getToday(),
        });
        
        if (insertError) {
          console.error('Error inserting logging entry:', insertError);
          alert(getUserFacingErrorMessage(insertError, 'Failed to log entry. Please try again.'));
          return;
        }
        setLogged(prev => {
          const newLogged = { ...prev };
          const set = new Set(newLogged[protocolId]);
          set.add(hackId);
          newLogged[protocolId] = set;
          return newLogged;
        });
        // XP reward logic (event key 1)
        const { data: xpEvent } = await supabase
          .from('xp_events')
          .select('event_id, xp_reward, name')
          .eq('event_id', 1)
          .maybeSingle();
        if (xpEvent) {
          // Get current count for this event
          const { data: existingEvent, error: selectError } = await supabase
            .from('user_xp_events')
            .select('count')
            .eq('xp_event_id', xpEvent.event_id)
            .eq('user_id', userId)
            .maybeSingle();
          
          if (selectError) {
            console.error('[ChecklistLogger] Error fetching existing XP event:', selectError);
          }
          
          const newCount = (existingEvent?.count || 0) + 1;
          
          // Upsert with incremented count - trigger will automatically update user_profiles.xp
          const { error: upsertError } = await supabase
            .from('user_xp_events')
            .upsert({
              xp_event_id: xpEvent.event_id,
              user_id: userId,
              count: newCount,
              updated_at: new Date().toISOString()
            }, { onConflict: 'xp_event_id,user_id' });

          if (upsertError) {
            console.error('[ChecklistLogger] Error upserting XP event:', upsertError);
            console.error('[ChecklistLogger] XP Event data:', { xp_event_id: xpEvent.event_id, user_id: userId, count: newCount });
          } else {
            setFloatingXP({ show: true, amount: xpEvent.xp_reward });
            const { data: profile } = await supabase
              .from('user_profiles')
              .select('xp, user_level')
              .eq('user_id', userId)
              .maybeSingle();
            if (profile) checkAndShowLevelUp(profile, xpEvent.xp_reward);
          }
        }
      } else {
        // Delete log
        const { error: deleteError } = await supabase.from('logging_hack_entries')
          .delete()
          .eq('user_id', userId)
          .eq('protocol_id', protocolId)
          .eq('hack_id', hackId)
          .eq('date', getToday());
        
        if (deleteError) {
          console.error('Error deleting logging entry:', deleteError);
          alert(getUserFacingErrorMessage(deleteError, 'Failed to unlog entry. Please try again.'));
          return;
        }
        setLogged(prev => {
          const newLogged = { ...prev };
          const set = new Set(newLogged[protocolId]);
          set.delete(hackId);
          newLogged[protocolId] = set;
          return newLogged;
        });
        // XP removal logic (event_id 1) — only per‑hack XP
        const { data: xpEvent } = await supabase
          .from('xp_events')
          .select('event_id, xp_reward, name')
          .eq('event_id', 1)
          .maybeSingle();
        if (xpEvent) {
          const { data: existingEvent } = await supabase
            .from('user_xp_events')
            .select('count')
            .eq('xp_event_id', xpEvent.event_id)
            .eq('user_id', userId)
            .maybeSingle();
          if (existingEvent) {
            const newCount = Math.max(0, existingEvent.count - 1);
            if (newCount > 0) {
              await supabase
                .from('user_xp_events')
                .update({
                  count: newCount,
                  updated_at: new Date().toISOString()
                })
                .eq('xp_event_id', xpEvent.event_id)
                .eq('user_id', userId);
            } else {
              await supabase
                .from('user_xp_events')
                .delete()
                .eq('xp_event_id', xpEvent.event_id)
                .eq('user_id', userId);
            }
          }
        }
      }
      if (onLogChange) onLogChange();
      window.dispatchEvent(new Event('activity-logged'));
    } catch (err) {
      // Optionally show error
    } finally {
      setActionLoading(prev => ({ ...prev, [`${protocolId}_${hackId}`]: false }));
    }
  };

  // Handle protocol checkbox (select/unselect all)
  const handleProtocolCheck = async (protocolId, allHackIds, checkedAll) => {
    setActionLoading(prev => {
      const updates = { ...prev };
      allHackIds.forEach(hid => { updates[`${protocolId}_${hid}`] = true; });
      return updates;
    });
    try {
      if (checkedAll) {
        // Insert all missing logs
        const toInsert = allHackIds.filter(hid => !logged[protocolId]?.has(hid)).map(hid => ({
          user_id: userId,
          protocol_id: protocolId,
          hack_id: hid,
          date: getToday(),
        }));
        if (toInsert.length > 0) await supabase.from('logging_hack_entries').insert(toInsert);
        setLogged(prev => {
          const newLogged = { ...prev };
          newLogged[protocolId] = new Set(allHackIds);
          return newLogged;
        });
        // Award XP for each new hack (event_id 1) only
        if (toInsert.length > 0) {
          const { data: xpEvent } = await supabase
            .from('xp_events')
            .select('event_id, xp_reward, name')
            .eq('event_id', 1)
            .maybeSingle();
          if (xpEvent) {
            const { data: existingEvent } = await supabase
              .from('user_xp_events')
              .select('count')
              .eq('xp_event_id', xpEvent.event_id)
              .eq('user_id', userId)
              .maybeSingle();
            const newCount = (existingEvent?.count || 0) + toInsert.length;
            const { error: upsertErr } = await supabase
              .from('user_xp_events')
              .upsert({
                xp_event_id: xpEvent.event_id,
                user_id: userId,
                count: newCount,
                updated_at: new Date().toISOString()
              }, { onConflict: 'xp_event_id,user_id' });
            if (!upsertErr) {
              const totalXp = toInsert.length * (xpEvent.xp_reward ?? 0);
              setFloatingXP({ show: true, amount: totalXp });
              const { data: profile } = await supabase
                .from('user_profiles')
                .select('xp, user_level')
                .eq('user_id', userId)
                .maybeSingle();
              if (profile) checkAndShowLevelUp(profile, totalXp);
            }
          }
        }
      } else {
        // Delete all logs for this protocol for today
        await supabase.from('logging_hack_entries')
          .delete()
          .eq('user_id', userId)
          .eq('protocol_id', protocolId)
          .eq('date', getToday());
        setLogged(prev => {
          const newLogged = { ...prev };
          newLogged[protocolId] = new Set();
          return newLogged;
        });
      }
      if (onLogChange) onLogChange();
      window.dispatchEvent(new Event('activity-logged'));
    } catch (err) {
      // Optionally show error
    } finally {
      setActionLoading(prev => {
        const updates = { ...prev };
        allHackIds.forEach(hid => { updates[`${protocolId}_${hid}`] = false; });
        return updates;
      });
    }
  };

  // Removed toggleCompleted function - completed items are now always visible

  if (!open) return null;
  const hasPlanEntries = (scheduleEntries.scheduled.length + scheduleEntries.unscheduled.length) > 0;

  return (
    <>
      <FloatingXPAnimation
        show={floatingXP.show}
        xpAmount={floatingXP.amount}
        onComplete={() => setFloatingXP({ show: false, amount: 0 })}
      />
      <LevelUpSuccessPopup
        show={showLevelUpPopup}
        level={levelUpLevel}
        onComplete={() => { setShowLevelUpPopup(false); setLevelUpLevel(null); }}
      />
      {!isPageMode && <div className="checklist-logger-backdrop" onClick={onClose}></div>}
      <div className={`checklist-logger-overlay ${isPageMode ? 'checklist-logger-page-mode' : ''}`}>
        <div className="checklist-logger-header">
          <div>
            <div className="checklist-logger-title">Today's logging plan</div>
            <div className="checklist-logger-subtitle">Confirm each biohack/protocol as you complete it</div>
          </div>
          {!isPageMode && (
            <button
              className="close-checklist-btn"
              onClick={onClose}
            >
              <Icon name="X" className="icon--sm" />
            </button>
          )}
        </div>
        
        <div className="checklist-logger-content">
          {loading ? (
            <div className="checklist-logger-loading">
              <JellyfishLoader />
            </div>
          ) : error ? (
            <div style={{ color: 'red', textAlign: 'center', padding: '20px' }}>{error}</div>
          ) : protocols.length === 0 ? (
            <div className="checklist-logger-empty">
              <div className="checklist-logger-empty-icon"></div>
              <button
                className="checklist-logger-empty-cta-button"
                onClick={() => navigate('/protocols/create')}
                aria-label="Create protocol"
              >
                Create Protocol
              </button>
              <div className="checklist-logger-empty-text">No active protocols</div>
              <div className="checklist-logger-empty-subtext">Create a protocol to start tracking your progress</div>
            </div>
          ) : (
            <div className="tree-container">
              <div className="checklist-logging-plan">
                {scheduleWarning ? (
                  <div className="checklist-logging-plan-warning">
                    <Icon name="AlertTriangle" className="checklist-logging-plan-warning-icon" />
                    <span>{scheduleWarning}</span>
                  </div>
                ) : !hasPlanEntries ? (
                  <div className="checklist-logging-plan-empty">
                    <span>No reminders yet.</span>
                    <button
                      type="button"
                      className="checklist-logging-plan-link"
                      onClick={() => {
                        onClose();
                        window.location.href = '/user#logging';
                      }}
                    >
                      Set logging times
                    </button>
                  </div>
                ) : (
                  <div className="checklist-logging-plan-list">
                    {(() => {
                      // Combine scheduled and unscheduled entries, only show biohack entries
                      const allHackEntries = [
                        ...scheduleEntries.scheduled.filter(entry => entry.hackId),
                        ...scheduleEntries.unscheduled.filter(entry => entry.hackId)
                      ];

                      // Group all entries by protocol
                      const groupedByProtocol = allHackEntries.reduce((groups, entry) => {
                        const protocolId = entry.protocolId;
                        if (!groups[protocolId]) {
                          groups[protocolId] = {
                            protocolName: entry.protocolName,
                            entries: []
                          };
                        }
                        groups[protocolId].entries.push(entry);
                        return groups;
                      }, {});

                      // Render all protocols together
                      return Object.entries(groupedByProtocol).map(([protocolId, protocolData]) => {
                        // Check if protocol has any scheduled entries
                        const hasSchedule = protocolData.entries.some(entry => entry.time && !entry.isUnscheduled);
                        // Get the protocol object for navigation
                        const protocol = protocolsById.get(protocolId);
                        
                        return (
                        <div key={protocolId} className="checklist-logging-plan-group">
                          <div className="checklist-logging-plan-group-header">
                            <span>{protocolData.protocolName}</span>
                            {!hasSchedule && protocol && (
                              <button
                                type="button"
                                className="checklist-logging-plan-create-schedule-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Don't call onClose() here: on mobile it's navigate(-1), which can
                                  // take the user to home and prevent opening the schedule. Navigate
                                  // directly so we land on UserPage, then open the schedule slide-over.
                                  navigate('/user');
                                  setTimeout(() => {
                                    window.dispatchEvent(new CustomEvent('open-protocol-schedule', {
                                      detail: { protocolId: protocolId, protocol: protocol }
                                    }));
                                  }, 150);
                                }}
                                title="Create schedule"
                                aria-label="Create schedule for this protocol"
                              >
                                <Icon name="Clock" className="checklist-logging-plan-create-schedule-icon" />
                                <span>Create schedule</span>
                              </button>
                            )}
                          </div>
                          <div className="checklist-logging-plan-group-items">
                            {protocolData.entries.map(entry => {
                              const completed = isEntryCompleted(entry);
                              const loadingAction = isEntryLoading(entry);
                              return (
                                <div
                                  key={entry.id}
                                  className={`checklist-logging-plan-item ${completed ? 'completed' : ''}`}
                                >
                                  {entry.time ? (
                                    <div className="checklist-logging-plan-time">
                                      {formatTimeDisplay(entry.time)}
                                      {!entry.hackName && entry.isDefault && (
                                        <span className="checklist-logging-plan-time-default">default</span>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="checklist-logging-plan-time">
                                      <span className="checklist-logging-plan-time-default">no schedule</span>
                                    </div>
                                  )}
                                  <div className="checklist-logging-plan-biohack">
                                    {entry.hackName}
                                  </div>
                                  <div className="checklist-logging-plan-action-wrapper">
                                    <button
                                      type="button"
                                      className={`checklist-logging-plan-action ${completed ? 'completed' : ''}`}
                                      onClick={() => handlePlanLog(entry)}
                                      disabled={loadingAction}
                                      aria-pressed={completed}
                                      aria-label={completed ? 'Unlog this entry' : 'Log this entry'}
                                    >
                                      {loadingAction ? (
                                        <Icon name="Loader2" className="checklist-logging-plan-action-icon spinning" />
                                      ) : completed ? (
                                        <Icon name="Check" className="checklist-logging-plan-action-icon" />
                                      ) : null}
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                      });
                    })()}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Checklist Log Disclaimer Modal - Rendered via Portal */}
      {showDisclaimerModal && createPortal(
        <div className="modal-overlay" onClick={(e) => {
          // Only prevent propagation if clicking directly on overlay (not modal content)
          // Don't close on overlay click - user must explicitly choose
          if (e.target === e.currentTarget) {
            e.stopPropagation();
          }
        }} style={{ zIndex: 10000 }}>
          <div className="modal-content" onClick={(e) => {
            e.stopPropagation();
          }} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Checklist Log Disclaimer</h3>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              <div style={{ 
                marginBottom: '24px', 
                fontSize: '14px', 
                lineHeight: '1.6', 
                color: 'var(--color-text-primary)',
                whiteSpace: 'pre-line'
              }}>
                {disclaimerText}
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '12px', 
                  cursor: 'pointer',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: 'var(--color-text-primary)'
                }}>
                  <input
                    type="checkbox"
                    checked={acceptResponsibility}
                    onChange={(e) => setAcceptResponsibility(e.target.checked)}
                    style={{ 
                      marginTop: '2px',
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                  />
                  <span>I understand and accept responsibility for logging this entry</span>
                </label>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '12px', 
                  cursor: 'pointer',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: 'var(--color-text-primary)'
                }}>
                  <input
                    type="checkbox"
                    checked={doNotShowAgain}
                    onChange={(e) => setDoNotShowAgain(e.target.checked)}
                    style={{ 
                      marginTop: '2px',
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                  />
                  <span>Do not show again</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  type="button"
                  className="btn btn--secondary btn--md btn--rounded"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleDisclaimerCancel();
                  }}
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn--primary btn--md btn--rounded"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleDisclaimerAccept();
                  }}
                  disabled={!acceptResponsibility}
                  style={{ flex: 1 }}
                >
                  Accept
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ChecklistLogger; 